import { formatUtcDatetimeToColombian } from "@/helpers/time";
import { Badge, Box, HStack, Text, VStack } from "@chakra-ui/react";
import type { ParticipantRole } from "@src/backend/src/protect/data/participants";
import { formatPhone } from "../columns";
import {
  ScreeningDocumentType,
  documentLabel,
} from "../mappers/screening.mapper";
import { Screening } from "../types";

type Person = NonNullable<Screening["cosigners"]>[number];

const participantRoleLabels: Record<ParticipantRole, string> = {
  tenant: "Arrendatario",
  legalRepresentative: "Representante legal",
  cosigner: "Codeudor",
};

type Participant = {
  role: ParticipantRole;
  name: string;
  documentType?: ScreeningDocumentType;
  documentNumber: string;
  phone?: string;
  email?: string;
  authorizedAt?: string;
  awaitsAuthorization: boolean;
};

const fromPerson = (role: ParticipantRole, person: Person): Participant => ({
  role,
  name: person.name,
  documentType: person.documentType,
  documentNumber: person.documentNumber,
  phone: person.phone,
  email: person.email,
  authorizedAt: person.habeasData?.acceptedAt,
  awaitsAuthorization: !person.habeasData,
});

const buildParticipants = (screening: Screening): Participant[] => [
  {
    role: "tenant",
    name: screening.tenantName,
    documentType: screening.tenantDocumentType,
    documentNumber: screening.tenantDocumentNumber,
    phone: screening.tenantPhone,
    email: screening.tenantEmail,
    authorizedAt: screening.tenantHabeasData?.acceptedAt,
    awaitsAuthorization: false,
  },
  ...(screening.legalRepresentative
    ? [fromPerson("legalRepresentative", screening.legalRepresentative)]
    : []),
  ...(screening.cosigners ?? []).map((cosigner) =>
    fromPerson("cosigner", cosigner)
  ),
];

const AuthorizationStatus = ({
  participant,
}: {
  participant: Participant;
}): JSX.Element | null => {
  if (participant.authorizedAt)
    return (
      <VStack gap={1} align="end" flexShrink={0}>
        <Badge bg="#E3FFE4" color="#1D7A5C" size="md">
          Autorizó
        </Badge>
        <Text fontSize="xs" color="gray.500">
          {formatUtcDatetimeToColombian(participant.authorizedAt)}
        </Text>
      </VStack>
    );

  if (participant.awaitsAuthorization)
    return (
      <Badge bg="#FFF7D6" color="#E6AE22" size="md" flexShrink={0}>
        Pendiente de autorización
      </Badge>
    );

  return null;
};

const ParticipantCard = ({
  participant,
  showAuthorization,
}: {
  participant: Participant;
  showAuthorization: boolean;
}): JSX.Element => (
  <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
    <HStack justify="space-between" align="start" gap={3}>
      <Box minW={0}>
        <Text fontSize="xs" fontWeight={600} color="gray.500">
          {participantRoleLabels[participant.role]}
        </Text>
        <Text fontWeight={600} color="gray.700">
          {participant.name}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {documentLabel(participant.documentType, participant.documentNumber)}
          {participant.phone ? ` · ${formatPhone(participant.phone)}` : ""}
        </Text>
        {participant.email && (
          <Text fontSize="sm" color="gray.600" wordBreak="break-all">
            {participant.email}
          </Text>
        )}
      </Box>
      {showAuthorization && <AuthorizationStatus participant={participant} />}
    </HStack>
  </Box>
);

type Props = { screening: Screening; showAuthorization?: boolean };

const Participants = ({
  screening,
  showAuthorization = true,
}: Props): JSX.Element => (
  <VStack gap={3} align="stretch">
    {buildParticipants(screening).map((participant) => (
      <ParticipantCard
        key={`${participant.role}-${participant.documentNumber}`}
        participant={participant}
        showAuthorization={showAuthorization}
      />
    ))}
  </VStack>
);

export default Participants;
